import type { Communication } from '../../../../services/communications';
import { formatCreatedDate } from '../../../../utils/dates';
import { channelDef } from './channels';
import { MetaCard } from './MetaCard';

interface RecentCommunicationsSectionProps {
    practiceId: string;
    communications: Communication[];
    isLoading: boolean;
    onNavigate: (path: string) => void;
}

function RecentRow({
    practiceId,
    communication,
    onNavigate
}: {
    practiceId: string;
    communication: Communication;
    onNavigate: (path: string) => void;
}) {
    const def = communication.lastEntry ? channelDef(communication.lastEntry.channel) : null;
    const Icon = def?.icon;

    return (
        <button
            type="button"
            onClick={() =>
                onNavigate(`/accounts/practices/${practiceId}/communications/${communication.id}`)
            }
            className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex flex-col gap-1"
        >
            <span className="flex items-center gap-2">
                {Icon && def && (
                    <span
                        className="w-4 h-4 rounded flex items-center justify-center shrink-0"
                        style={{ background: def.soft, color: def.tone }}
                    >
                        <Icon className="h-2.5 w-2.5" />
                    </span>
                )}
                <span className="text-[12px] font-semibold text-gray-800 truncate flex-1">
                    {communication.title}
                </span>
                <span className="text-[11px] text-gray-400 shrink-0">
                    {formatCreatedDate(communication.lastActivityAt)}
                </span>
            </span>
            <span className="text-[11px] text-gray-500 truncate w-full">
                {communication.entryCount}{' '}
                {communication.entryCount === 1 ? 'interaction' : 'interactions'}
                {communication.lastEntry ? ` · last by ${communication.lastEntry.authorName}` : ''}
            </span>
        </button>
    );
}

// A quick prior-history nudge, not a full history feature. Hidden when
// there's nothing to show or on the practice-less variant.
export function RecentCommunicationsSection({
    practiceId,
    communications,
    isLoading,
    onNavigate
}: RecentCommunicationsSectionProps) {
    if (isLoading || communications.length === 0) return null;

    return (
        <MetaCard title="Recent from this practice" pad={false}>
            <div className="divide-y divide-gray-100">
                {communications.map((communication) => (
                    <RecentRow
                        key={communication.id}
                        practiceId={practiceId}
                        communication={communication}
                        onNavigate={onNavigate}
                    />
                ))}
            </div>
        </MetaCard>
    );
}
